import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, tap, switchMap, mergeMap, withLatestFrom, debounceTime, delay } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { NgxPermissionsService } from 'ngx-permissions';

import * as fromApp from '@reducers/index'
import { AuthService } from '@services/auth/auth.service'
import { AuthActions, RouterActions, BusinessActions, ResellersActions } from '@actions/index'
import { Credentials, ResellerModel, BusinessModel, UserModel } from '@models/index';
import { Role } from 'src/app/core/enums/role.enum';
import { RouterFacadeService } from '@services/router-facade.service';
import { HttpApiService } from '@services/http-api.service';
import { AuthFacadeService } from '@services/auth/auth-facade.service';
import { AppFacadeService } from '@services/app-facade.service';
import { LocationsFacadeService } from '@services/locations/locations-facade.service'
import { LocationBusinessMetadata } from '@actions/auth/auth.actions';

/**
 * AuthEffects handles all effects related
 * to authentication and dispatches the 
 * corrspondent actions or services.
 */
@Injectable()
export class AuthEffects {

  /**
   * Pipes all [Login Page] Login
   * actions and dispatches the {@link AuthService}
   * to log in the user. When the petition
   * is succesfull it dispatches the [Login Page] Login Success
   * action to store the logged user value on the app state
   * and distaptch loginSuccess$ effect. If the petition returns
   * an error the effect dispatches [Login Page] Login Failure
   * to store the error on auth's app state.
   */
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      map(action => action.credentials),
      exhaustMap(payload =>
        this.authService
          .login(payload)
          .pipe(
            map(user => AuthActions.loginSuccess({ user })),
            catchError(error => {
              this.store.dispatch(AuthActions.logOut());
              const message = error.error ? error.error.message : error.message;
              return of(AuthActions.loginFailure({ error: message }));
            })
          )
      )
    )
  );

  /**
   * Pipes all [Login Page] Login Success
   * actions and performs the following
   * steps:
   * - Sets the last activity time which
   * is used to log the user out due to
   * innactivity
   * - If user is not a Reseller Admin
   * or a Site Admin and is a multi business
   * user then the app selects the reseller 
   * and leads the user to {@link SelectBusinessComponent}
   * - If the user is a Site Admin then 
   * the user will be leaded to {@link AdminDashboardComponent}
   * - If the user is a Reseller Admin
   * then the app will select the correspondent
   * reseller and lead the user to {@link ResellerDashboardComponent}
   * - If the user is a single business one
   * then the app will select the reseller, and the
   * business. Depending on business number of
   * locations the user will be leaded to {@link BusinessDashboardComponent}
   * there is just one location, otherwise will be 
   * leaded to {@link BusinessLocationsComponent}.
   */
  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        withLatestFrom(
          this.store.select(fromApp.getSelectedBusinessProfile),
        ),
        tap(([{ user }, selectedBusiness]) => {
          this.appFacadeService.setLastTimeActive()
          this.permissionsService.loadPermissions(user.permissions)
          console.log("[auth.effect loginSuccess$] user:", user)

          if (user.reseller.favIcon) {
            const appFavicon = document.getElementById('appFavicon');

            if (appFavicon) {
              appFavicon.setAttribute('href', user.reseller.favIcon);
            }
          }

          // TODO: DRY with same code that runs normally when navigation to "/" and already logged in
          switch (user.role.name) {
            case Role.SiteAdmin:
              this.store.dispatch(RouterActions.navigateTo({ pathUrl: "/admin/reseller" }));
              break
            case Role.Reseller:
              this.store.dispatch(ResellersActions.selectResellerProfile({ reseller: user.reseller }));
              this.routerFacadeService.navigateTo$('/reseller/business');
              this.store.dispatch(AuthActions.searchLocationAutocompleteOptions({}));
              break;
            default:
              this.store.dispatch(ResellersActions.selectResellerProfile({ reseller: user.reseller }))
              this.store.dispatch(BusinessActions.searchBusinessesSuccess({
                data: user.businesses.map(({ business }) => business),
                total: 0,
              }))

              const locationCount = (user.businesses || []).reduce((total, business) => {
                return total + business.countLocations;
              }, 0);
              console.log('locationCount:', locationCount);

              if (user.businesses && user.businesses.length > 0) {
                setTimeout(() => {
                  this.store.dispatch(BusinessActions.selectBusinessProfile({
                    business: user.businesses[0].business
                  }))
                  this.permissionsService.loadPermissions(user.businesses[0].permissions)

                  // todo: workaround for multi-business users
                  // in setTimeout to ensure store.dispatch for selectBusinessProfile works
                  setTimeout(() => {
                    if (user.businesses.length > 1) {
                      window.location.href = '/admin-business/locations';
                      return;
                    }
                  });

                  console.log('user.businesses[0].location', user.businesses[0].location);

                  if (user.businesses[0].location) {
                    this.store.dispatch(RouterActions.navigateBusinessDashboard())
                    this.locationFacadeServide.setCurrentLocation(user.businesses[0].location);
                  } else {
                    this.routerFacadeService.navigateTo$('/admin-business/locations')
                  }
                })
              }

              break;
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Pipes all [Login Page] Send Email Recover 
   * Password actions and dispatches the 
   * {@link AuthService} to send the user via
   * email the recovery code. After this
   * the user is redirected to {@link ForgotPasswordSubmitComponent}.
   * When the petition is succesfull it dispatches the [Login Page]
   * Send Email Password Recover Success action. If the petition returns
   * an error the effect dispatches [Login Page] Login Failure
   * to store the error on auth's app state.
   */
  sendEmailPasswordRecover$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.sendEmailPasswordRecover),
      exhaustMap(data =>
        this.authService
          .sendEmailPasswordRecover(data.email, data.resellerId)
          .pipe(
            tap(() => this.routerFacadeService
              .navigateTo$('auth/recovery-pass-confirm')
            ),
            map(sent => AuthActions.sendEmailPasswordRecoverSuccess({ sent })),
            catchError(result => of(AuthActions.loginFailure({ error: result.error.message })))
          )
      )
    )
  );

  /**
  * Pipes all [Login Page] Confirm New Password
  * actions and dispatches the {@link AuthService} 
  * to recover user password. When the petition is
  * succesfull it dispatches the [Login Page] Login
  * and  the [Login Page] Confirm New Password Success
  * actions. If the petition returns
  * an error the effect dispatches [Login Page] Login Failure
  * to store the error on auth's app state.
  */
  confirmNewPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmNewPassword),
      map(action => action.payload),
      exhaustMap((payload) =>
        this.authService
          .confirmNewPassword(payload)
          .pipe(
            mergeMap(pswChanged => [
              RouterActions.navigateSignUpProcess(),
              AuthActions.login({
                credentials: {
                  username: payload.username,
                  password: payload.newPassword,
                  resellerId: payload.resellerId
                }
              }),
              AuthActions.confirmNewPasswordSuccess({ pswChanged })
            ]),
            catchError(error => of(AuthActions.loginFailure({ error: error.message })))
          )
      )
    )
  );

  /**
   * Pipes all [Login Page] Change Password
   * actions and dispatches the {@link AuthService} 
   * to change user password. When the petition is
   * succesfull it dispatches the [Login Page] Change 
   * Password Succes action that will set a boolean
   * value on the app state that indicates the password
   * changed successfully. If the petition returns
   * an error the effect dispatches [Login Page] Login Failure
   * to store the error on auth's app state.
   */
  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      map(action => action.payload),
      exhaustMap((payload) =>
        this.authService.changeNewPassword(payload.psw, payload.newPsw).pipe(
          map(_pswChanged => AuthActions.changePasswordSuccess()),
          catchError(error => of(AuthActions.loginFailure({ error: error.message })))
        )
      )
    )
  );

  /**
   * Pipes all [Login Page] Validate Account
   * actions and dispatches the {@link AuthService} 
   * to validate user account via cognito. When the petition is
   * succesfull it dispatches the [Login Page] Validate Account Success
   * action that will set a boolean value on the app state that 
   * indicates the user account is confirmed. If the petition returns
   * an error the effect dispatches [Login Page] Login Failure
   * to store the error on auth's app state.
   */
  validateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.validateAccount),
      switchMap(({ payload }) =>
        this.authService
          .confirmSignUp(payload)
          .pipe(
            map(isValidate => AuthActions.validateAccountSuccess({ isValidate })),
            tap(() => this.store.dispatch(RouterActions.navigateAuth())),
            catchError(error => of(AuthActions.loginFailure({ error: error.message })))
          )
      )
    )
  );

  /**
   * Pipes all [Login Page] Log Out
   * actions and dispatches the {@link AuthService} 
   * to log the user out via cognito. When the petition is
   * succesfull it dispatches the [Login Page] Log Out Success
   * action that will set a boolean value on the app state that 
   * indicates that the user was logged out successsfully. 
   * If the petition returns an error the effect dispatches 
   * [Login Page] Login Failure to store the error on auth's app state.
   */
  logOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logOut),
      switchMap(() =>
        this.authService
          .logout()
          .pipe(
            map(loggedOut => AuthActions.logOutSuccess({ loggedOut })),
            catchError(error => of(AuthActions.loginFailure({ error: error.message })))
          )
      )
    )
  );

  /**
   * Pipes all [Login Page] sign Up
   * actions and dispatches the {@link AuthService} 
   * to sign up the user via cognito and notifies the
   * API through a HTTP petition. When the petition is
   * succesfull it dispatches the [Login Page] sign Up Success
   * and [Login Page] Login actions to log in the
   * user. If the petition returns an error the effect dispatches 
   * [Login Page] Login Failure to store the error on auth's app state.
   */
  signUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signUp),
      switchMap(({ payload }) =>
        this.authService
          .signUp(payload)
          .pipe(
            switchMap(data => this.httpApiService.postRequest(
              {
                params: `users/signup`,
                payload: {
                  ...payload,
                  signed: true
                }
              }
            )),
            mergeMap(data => [
              AuthActions.signUpSuccess({ data }),
              RouterActions.navigateSignUpProcess(),
              AuthActions.login({
                credentials: {
                  username: payload.email,
                  password: payload.password,
                  resellerId: payload.resellerId
                }
              }),
            ]),
            catchError(error => of(AuthActions.loginFailure({ error: error.message })))
          )
      )
    )
  );

  /**
  * Pipes all [Login Page] Get Sign up User
  * actions and dispatches the {@link AuthService} 
  * to get the user configuration data through the API. 
  * When the petition is succesfull it dispatches the 
  * [Login Page] Select User Profile action to fill the
  * sign up form. If the petition returns an error the effect dispatches 
  * [Login Page] Login Failure to store the error on auth's app state.
  */
  getSignUpUSer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getSignUpUser),
      map(action => action.payload),
      switchMap(payload =>
        this.authService
          .getSignUpUser(payload)
          .pipe(
            map(user => AuthActions.loginSuccess({ user })),
            catchError(error => of(AuthActions.loginFailure({ error: error.message })))
          )
      )
    )
  );

  /**
   * Pipes all [Login Page] Get Reseller
   * actions and dispatches a HTTP petition
   * to get the reseller which the user belongs to. 
   * When the petition is succesfull it dispatches the 
   * [Login Page] Get Reseller Success action to 
   * store selected reseller on the app state. 
   * If the petition returns an error the effect dispatches 
   * [Login Page] Login Failure to store the error on auth's app state.
   */
  getReseller$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getReseller),
      switchMap(({ request }) =>
        this.httpApiService
          .getRequest(request)
          .pipe(
            map(data => data as { id: string, name: string, shortName: string, logo: string, siteURL: string, favIcon: string }),
            map(reseller => AuthActions.getResellerSuccess(reseller)),
            // catchError(error => of(AuthActions.usersError({ error: error.message })))
          )
      )
    )
  );

  /**
   * @ignore
   */
  searchLocationAutocompleteOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.searchLocationAutocompleteOptions),
      switchMap(({ resellerId }) => {
        return this.authService.searchLocationAutocompleteOptions$(resellerId).pipe(
          map(locationAutocompleteOptions => {
            const locationMetadata = Object.values(locationAutocompleteOptions
              .reduce((obj, option) => {
                if (!obj[option.businessId]) obj[option.businessId] = {
                  businessId: option.businessId,
                  businessName: option.businessName,
                  locations: []
                };

                obj[option.businessId].locations.push(option);
                return obj;
              }, {} as {
                [businessId: string]: {
                  businessId: string,
                  businessName: string,
                  locations: LocationBusinessMetadata[]
                }
              })
            );

            console.log("locationMetadata:", locationMetadata);

            return AuthActions.searchLocationAutocompleteOptionsSuccess({ locationMetadata });
          })
        );
      })
    )
  );

  /**
   * Pipes all [Login Page] Get Logged User Data
   * actions and dispatches a HTTP petition
   * to get the logged user data. 
   * When the petition is succesfull it dispatches the 
   * [Login Page] Login Success action to 
   * store logged user data on the app state. 
   */
  getLoggedUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getLoggedUserData),
      switchMap(({ request }) =>
        this.httpApiService
          .getRequest(request)
          .pipe(
            map(data => data as UserModel),
            map(user => AuthActions.loginSuccess({ user })),
            // catchError(error => of(AuthActions.usersError({ error: error.message })))
          )
      )
    )
  );


  /**
   * AuthEffects Constructor
   * @param actions$ 
   * @param authService The app authentication service
   * @param authFacadeService The app authentication facade service
   * @param appFacadeService The app facade service to set inactivity time and opne side bar
   * @param routerFacadeService The app router facade service to redirect the user to different app views depending on the role and businesses-locations conditions
   * @param locationFacadeServide The location facade service to select the current location
   * @param store The app storage
   * @param permissionsService The app permissions service to load logged user permissions
   * @param httpApiService The HTTP service to build HTTP requests
   */
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private authFacadeService: AuthFacadeService,
    private appFacadeService: AppFacadeService,
    private routerFacadeService: RouterFacadeService,
    private locationFacadeServide: LocationsFacadeService,
    private store: Store<any>,
    private permissionsService: NgxPermissionsService,
    private httpApiService: HttpApiService,
  ) { }
}
